import type { TranslationKey } from '#core/i18n/lang';
import { TranslationServiceProvider } from '#infra/service-providers/translation/translation.service.provider';
import type { AvailableLanguagesType } from '#root/global.type';
import { createGlobalState } from '@vueuse/core';
import dayjs from 'dayjs';
import type { TOptions } from 'i18next';
import { useTranslation } from 'i18next-vue';
import { computed, ref } from 'vue';

export const useLanguage = createGlobalState(() => {
  const { i18next, t } = useTranslation();
  const translationService = new TranslationServiceProvider();
  const isLoading = ref(false);

  const adapter: Record<AvailableLanguagesType, string> = {
    fr: 'fr-FR',
    en: 'en-GB',
    es: 'es-ES'
  } as const;

  return {
    setLanguage: async (language: AvailableLanguagesType) => {
      isLoading.value = true;
      try {
        const translations = await translationService.getTranslations(language);

        i18next.addResourceBundle(language, 'translation', translations, true, true);

        dayjs.locale(language);
        await i18next.changeLanguage(language);
      } catch (error) {
        console.error(`Failed to load translations for ${language}:`, error);
        dayjs.locale(language);
        await i18next.changeLanguage(language);
      } finally {
        isLoading.value = false;
      }
    },

    language: computed(() => i18next.resolvedLanguage as AvailableLanguagesType),

    languages: Object.keys(i18next.services.resourceStore.data) as AvailableLanguagesType[],

    t: t as (key: TranslationKey | string, options?: TOptions) => string,

    localLanguage: computed(() => {
      const language = i18next.resolvedLanguage as AvailableLanguagesType | undefined;
      return (language && adapter[language]) ?? adapter['en'];
    }),

    isLoading: computed(() => isLoading.value)
  };
});
